import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  UnitWorkflowConfigs,
  WorkflowApprovalStepConfig,
  WorkflowConfig,
  WorkflowStepConfig,
} from '~/data/workflows';
import {
  WorkflowProcessConfigState,
  getWorkflowProcessTypeTranslationKeyFromString,
  getWorkflowRolesTranslationKeyFromString,
} from '~/scenes/AdminModule/components/Views/WorkflowsConfigurationView/data';

export type WorkflowTableDataRow = {
  id: string;
  type: string;
  initiators: string;
  stepsSummary: string;
  closer: string;
  overseer: string;
  administrator: string;
  meta: {
    activeUnitConfig: boolean;
    configState: WorkflowProcessConfigState;
    configData: WorkflowConfig;
  };
};

export type UseWorkflowConfigurationsTableOutput = {
  gridData: WorkflowTableDataRow[];
};

type PropsType = {
  unitWorkflowConfigs: UnitWorkflowConfigs;
};

export const useWorkflowConfigurationsGridData = (
  props: PropsType,
): UseWorkflowConfigurationsTableOutput => {
  const { t } = useTranslation();

  const { unitWorkflowConfigs } = props;

  const translateRoleName = useCallback(
    (roleName: string) => t(getWorkflowRolesTranslationKeyFromString(roleName)),
    [t],
  );
  const resolveRoleNames = useCallback(
    (step: WorkflowStepConfig | WorkflowApprovalStepConfig): string => {
      const { roleNames } = step;
      return roleNames
        .map((roleName) => translateRoleName(roleName))
        .join(', ');
    },
    [translateRoleName],
  );

  const translateProcessType = useCallback(
    (processType: string) =>
      t(getWorkflowProcessTypeTranslationKeyFromString(processType)),
    [t],
  );

  const resolveConfigState = useCallback(
    (config: WorkflowConfig): WorkflowProcessConfigState => {
      if (config.unitId === unitWorkflowConfigs.unitId) {
        return WorkflowProcessConfigState.Unit;
      }

      if (config.unitId > 0) {
        return WorkflowProcessConfigState.Inherited;
      }

      return WorkflowProcessConfigState.Undefined;
    },
    [unitWorkflowConfigs],
  );

  const gridData = useMemo((): WorkflowTableDataRow[] => {
    const getStepsSummaryValue = (count: number) => {
      return t('approvalsRequired_count', {
        count: count,
      });
    };

    return unitWorkflowConfigs.workflows.map((config) => {
      const row: WorkflowTableDataRow = {
        id: config.id,
        type: translateProcessType(config.processType),
        initiators: resolveRoleNames(config.workflow.initiators),
        stepsSummary: getStepsSummaryValue(
          config.workflow.approvalSteps.length,
        ),
        closer: resolveRoleNames(config.workflow.closer),
        overseer: resolveRoleNames(config.workflow.overseer),
        administrator: resolveRoleNames(config.workflow.administrator),
        meta: {
          activeUnitConfig: config.unitId === unitWorkflowConfigs.unitId,
          configState: resolveConfigState(config),
          configData: config,
        },
      };

      return row;
    });
  }, [
    resolveRoleNames,
    resolveConfigState,
    translateProcessType,
    unitWorkflowConfigs,
    t,
  ]);

  return {
    gridData,
  };
};
